import { useEffect } from 'react';
import { ImpactMetric } from '../types/careerCard';

interface UseImpactMetricsKeyboardProps {
  isEditMode: boolean;
  careerImpact: ImpactMetric;
  engagementLevel: ImpactMetric;
  onMetricChange: (type: 'career_impact' | 'engagement_level', value: number) => void;
}

export const useImpactMetricsKeyboard = ({
  isEditMode,
  careerImpact,
  engagementLevel,
  onMetricChange,
}: UseImpactMetricsKeyboardProps) => {
  useEffect(() => {
    if (!isEditMode) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle Alt key combinations
      if (!e.altKey) return;

      // Helper function to ensure we only change by 1 and stay within bounds
      const getNewValue = (currentValue: number, increment: boolean): number => {
        const newValue = increment ? currentValue + 1 : currentValue - 1;
        return Math.min(Math.max(newValue, 1), 5);
      };

      let handled = false;

      switch (e.key) {
        case 'ArrowUp':
          // Only update career impact
          onMetricChange('career_impact', getNewValue(careerImpact.value, true));
          handled = true;
          break;
        case 'ArrowDown':
          // Only update career impact
          onMetricChange('career_impact', getNewValue(careerImpact.value, false));
          handled = true;
          break;
        case 'ArrowRight':
          // Only update engagement level
          onMetricChange('engagement_level', getNewValue(engagementLevel.value, true));
          handled = true;
          break;
        case 'ArrowLeft':
          // Only update engagement level
          onMetricChange('engagement_level', getNewValue(engagementLevel.value, false));
          handled = true;
          break;
      }

      if (handled) {
        // Prevent default browser behavior and stop propagation
        e.preventDefault();
        e.stopPropagation();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isEditMode, careerImpact.value, engagementLevel.value, onMetricChange]);
}; 